import { useEffect, useMemo, useState } from "react";
import { formatBetSlipPoint } from "../../../utility/odds";
import { IoIosAdd, IoIosClose } from "react-icons/io";
import { Modal } from "react-bootstrap";
import { isDTS } from "../../../utility/platform";
import {
  getEntryTypeLabel,
  getFeaturedMarketLabel,
  getTerm,
} from "../../../utility/complianceCopy";
import { formatMoney, roundMoney } from "../../lib/wagerFinancials";
import {
  calcParlayCombinedAmericanOdds,
  calcParlayPayout,
  formatAmericanOdds,
  getLegPricesFromSelections,
} from "../../../utility/parlayOdds";

// Compute American-odds win amount given a risk (wager) and price.
const americanPayout = (risk, price) => {
  const bet = Number(risk);
  const odds = Number(price);
  if (!bet || !odds) return { win: 0, payout: 0 };
  const win = odds > 0 ? (odds / 100) * bet : (100 / Math.abs(odds)) * bet;
  return {
    win: roundMoney(win),
    payout: roundMoney(bet + win),
  };
};

const getSelectionLabel = (item) => {
  if (!item) return "Selection";
  const team = item.teamName || item.homeTeam || item.home_team || "Selection";
  const market =
    item.type === "h2h" || item.type === "spreads" || item.type === "totals"
      ? getFeaturedMarketLabel(item.type, isDTS())
      : item.type
      ? String(item.type).toUpperCase()
      : "";
  const point =
    item.type !== "h2h"
      ? (() => {
          const formatted = formatBetSlipPoint(item);
          return formatted && formatted !== "-" ? ` ${formatted}` : "";
        })()
      : "";
  return market ? `${team} — ${market}${point}` : `${team}${point}`;
};

export default function CalculatorModel({
  show,
  setShow,
  selections = [],
  onRemoveSelection,
  onApplyRisk,
  onAddOpenSlot,
  mode = "straight",
}) {
  const handleClose = () => setShow(false);
  const isParlay = mode === "parlay";

  const initialRisk = useMemo(() => {
    const first = selections.find((s) => Number(s?.Price) > 0);
    return first ? Number(first.Price) : 0;
  }, [selections]);

  const [risk, setRisk] = useState(initialRisk);

  useEffect(() => {
    if (show) setRisk(initialRisk);
  }, [show, initialRisk]);

  const legPrices = useMemo(
    () => getLegPricesFromSelections(selections),
    [selections]
  );

  const parlayTotals = useMemo(() => {
    const { win, payout } = calcParlayPayout(risk, legPrices);
    return {
      win: roundMoney(win),
      payout: roundMoney(payout),
      combinedOdds: calcParlayCombinedAmericanOdds(legPrices),
    };
  }, [risk, legPrices]);

  const rows = useMemo(
    () =>
      selections.map((item, index) => {
        const label = getSelectionLabel(item);
        const odds = item?.price;

        if (isParlay) {
          return {
            item,
            index,
            label,
            odds,
            win: null,
            payout: null,
          };
        }

        const { win, payout } = americanPayout(risk, item?.price);
        return {
          item,
          index,
          label,
          odds,
          win,
          payout,
        };
      }),
    [selections, risk, isParlay]
  );

  const straightTotals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          acc.win += Number.isFinite(r.win) ? r.win : 0;
          acc.payout += Number.isFinite(r.payout) ? r.payout : 0;
          return acc;
        },
        { win: 0, payout: 0 }
      ),
    [rows]
  );

  const totals = isParlay ? parlayTotals : straightTotals;

  const handleRemove = (item, index) => {
    if (typeof onRemoveSelection === "function") {
      onRemoveSelection(item, index);
    }
  };

  const handleGenerate = () => {
    if (typeof onApplyRisk === "function") {
      onApplyRisk(Number(risk) || 0);
    }
    handleClose();
  };

  const isEmpty = rows.length === 0;

  return (
    <Modal className="calculatorModel" show={show} onHide={handleClose} centered>
      <div className="calculatorModelHeader pb-2 d-flex align-items-center justify-content-between">
        <h2 className="fs18 m-0">PARLAY CALCULATOR</h2>
        <button className="modelCloseBtn" onClick={handleClose}>
          <IoIosClose fontSize={25} />
        </button>
      </div>

      <div className="calculatorModelBody">
        <div className="tableWrapper">
          <table>
            <thead>
              <tr className="mg-text-gray2 fs13">
                <th></th>
                <th>Selection</th>
                <th className="text-center">Odds</th>
                <th className="text-center">
                  {isParlay ? "Leg" : isDTS() ? getTerm("prize") : "Payout"}
                </th>
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 fs13">
                    No selections yet. Pick some odds from the board to
                    calculate a parlay.
                  </td>
                </tr>
              ) : (
                rows.map(({ item, index, label, odds, payout }) => (
                  <tr key={`${item?.CheckId || item?.id || index}-${index}`} className="fs13">
                    <td className="td-remove-btn">
                      <button
                        type="button"
                        onClick={() => handleRemove(item, index)}
                        aria-label="Remove selection"
                      >
                        <IoIosClose />
                      </button>
                    </td>
                    <td>
                      {index + 1}. {label}
                    </td>
                    <td className="ps-3 text-center">{formatOdds(odds)}</td>
                    <td className="ps-3 text-center">
                      {isParlay ? `#${index + 1}` : `$${formatMoney(payout)}`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!isEmpty && (
              <tfoot>
                <tr className="fs13 fw-bold">
                  <td></td>
                  <td>
                    {isParlay
                      ? `Combined ${formatAmericanOdds(totals.combinedOdds)}`
                      : "Total"}
                  </td>
                  <td className="ps-3 text-center">
                    Win: ${formatMoney(totals.win)}
                  </td>
                  <td className="ps-3 text-center">
                    ${formatMoney(totals.payout)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {typeof onAddOpenSlot === "function" && (
          <div className="addSlotPart">
            <button
              type="button"
              className="mg-primary-btn fs15"
              onClick={onAddOpenSlot}
            >
              <IoIosAdd size={25} />
              {isDTS()
                ? `Add Open ${getEntryTypeLabel("Parlay", true)} Slot`
                : "Add Open Parlay Slot"}
            </button>
          </div>
        )}

        <div className="inputWrapper d-flex align-align-items-center justify-content-between">
          <label className="d-flex align-items-center" htmlFor="modelRiskId">
            Risk
          </label>
          <input
            type="number"
            id="modelRiskId"
            min={0}
            step="0.01"
            value={risk}
            onChange={(e) => setRisk(e.target.value)}
          />
        </div>
      </div>

      <div className="calculatorModelFooter">
        <button
          type="button"
          className="mg-primary-btn"
          onClick={handleGenerate}
          disabled={isEmpty}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}

const formatOdds = (value) => formatAmericanOdds(value);
